<template>
  <div v-if="!isMspRoute">
    <div class="d-flex align-center">
      <v-icon class="mr-2" icon="$success" />
      <div class="subtitle1" v-html="$t(`modals.${SecurityGapAction.MANUAL_FIX}.fixDetails`)" />
    </div>
    <div
      v-if="config.item.securityGapType === SecurityGapType.UNLICENSED_DEVICES"
      class="body1 mt-4"
      data-testid="manual-fix-contact-provider"
    >
      {{ $t(`modals.${SecurityGapAction.MANUAL_FIX}.contactProviderDescription`) }}
    </div>
    <div v-else class="body1 mt-4" data-testid="manual-fix-go-to-settings">
      <span>{{ $t(`modals.${SecurityGapAction.MANUAL_FIX}.goToSettingsDescription1`) }}</span>
      <span
        class="coro-link"
        @click="goToSettings(workspace.workspaceId)"
        role="button"
        data-testid="manual-fix-go-to-settings-link"
      >
        {{ linkText.toLowerCase() }}
      </span>
      <span>{{ $t(`modals.${SecurityGapAction.MANUAL_FIX}.goToSettingsDescription2`) }}</span>
    </div>
    <div
      class="body1 mt-4 ml-6 direct-workspace-steps"
      data-testid="manual-fix-steps"
      v-html="$t(`modals.${SecurityGapAction.MANUAL_FIX}.steps.${config.item.securityGapType}`)"
    />
  </div>
  <div v-else>
    <div class="info-block pa-4">
      <div class="d-flex align-center mb-2">
        <v-icon class="mr-2" icon="$success" />
        <div class="body2" v-html="$t(`modals.${SecurityGapAction.MANUAL_FIX}.fixDetails`)" />
      </div>
      <div
        class="body2 ml-6"
        data-testid="manual-fix-steps-msp"
        v-html="
          $t(`modals.${SecurityGapAction.MANUAL_FIX}.stepsMsp.${config.item.securityGapType}`)
        "
      />
    </div>
    <div class="warning-block pa-4 mt-6">
      <div class="d-flex align-center">
        <v-icon class="mr-2" icon="$warning" />
        <div
          class="body2"
          data-testid="manual-fix-disclaimer"
          v-html="$t(`modals.${SecurityGapAction.MANUAL_FIX}.${disclaimerText}`)"
        />
      </div>
    </div>

    <div class="mt-6 mb-4 d-flex justify-space-between align-center">
      <div class="subtitle1">
        {{ $t(`modals.${SecurityGapAction.MANUAL_FIX}.affectedWorkspaces`) }}
      </div>
      <v-text-field
        density="compact"
        :placeholder="$t('general.search')"
        :aria-label="$t('general.search')"
        class="search-field"
        prepend-inner-icon="icon-search-xs"
        clearable
        clear-icon="icon-close-xs"
        hide-details
        @click.stop
        rounded
        max-width="280"
        data-testid="manual-fix-workspace-search"
        @update:model-value="workspaceSearch = $event?.trim().toLowerCase()"
      />
    </div>
    <table-wrapper>
      <v-data-table-server
        height="300"
        class="coronet-table--highlightable"
        :items="localValue.workspaces"
        :fixed-header="true"
        item-key="workspaceId"
        hide-default-footer
        :loading="loading"
        :items-length="totalWorkspaces"
      >
        <template #headers>
          <tr>
            <th>
              {{ $t("general.workspace") }}
            </th>
            <th>
              {{ $t(`modals.${SecurityGapAction.MANUAL_FIX}.link`) }}
            </th>
          </tr>
        </template>
        <template #item="{ item }">
          <tr :key="`${item.workspaceId}`" data-testid="manual-fix-workspace-row">
            <td>
              <span class="body2" data-testid="manual-fix-workspace-name">{{ item.name }}</span>
            </td>
            <td>
              <span
                class="coro-link"
                @click="goToSettings(item.workspaceId)"
                role="button"
                data-testid="manual-fix-workspace-link"
              >
                {{ linkText }}
              </span>
            </td>
          </tr>
        </template>
      </v-data-table-server>
    </table-wrapper>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { SecurityGapAction, SecurityGapType } from "@/constants/security-gaps.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import { type SecurityGapItem, useSecurityGapsStore } from "@/_store/security-gaps.module.ts";
import TableWrapper from "@/components/TableWrapper.vue";
import { storeToRefs } from "pinia";
import { navigateToRoute } from "@/_helpers/navigation";
import { isMspPortalRoute } from "@/_helpers/subscription";
import debounce from "lodash/debounce";
import cloneDeep from "lodash/cloneDeep";
import { RouteName } from "@/constants/routes.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useSecurityGapsNavigation } from "@/composables/useSecurityGapsNavigation.ts";
import { AgentHealthIssue } from "@/constants/devices.ts";

const securityGapTypeRouteMap: Partial<Record<SecurityGapType, RouteName>> = {
  [SecurityGapType.NO_SCHEDULED_SCAN_ADDED]:
    RouteName.ENDPOINT_DATA_GOVERNANCE_SENSITIVE_DATA_SCANS_TAB,
  [SecurityGapType.INITIAL_DEVICE_DATA_GOVERNANCE_CONFIGURATION_INCOMPLETE]:
    RouteName.ENDPOINT_DATA_GOVERNANCE_PRIVACY_SENSITIVE_DATA_TAB,
  [SecurityGapType.DEVICE_DATA_GOVERNANCE_TYPES_NOT_CONFIGURED]:
    RouteName.ENDPOINT_DATA_GOVERNANCE_PRIVACY_SENSITIVE_DATA_TAB,
  [SecurityGapType.NO_ENDPOINTS_ADDED_FOR_MONITORING]:
    RouteName.DEVICES_SETTINGS_AGENT_DEPLOYMENT_TAB,
  [SecurityGapType.NO_PROTECTED_USERS_ADDED]: RouteName.USERS_SETTINGS_USERS_TAB,
  [SecurityGapType.NO_CLOUD_APPLICATION_CONNECTED]: RouteName.CLOUD_APPS_PAGE,
  [SecurityGapType.CLOUD_APPLICATION_CONNECTED_ADDITIONAL_SETUP_REQUIRED]:
    RouteName.CLOUD_APPS_PAGE,
  [SecurityGapType.ACCESS_PERMISSIONS_SETTINGS_NOT_ADDED]:
    RouteName.CLOUD_SECURITY_PERMISSIONS_LIST_TAB,
  [SecurityGapType.ACCESS_PERMISSIONS_ADDED_NO_AUTO_REMEDIATION_SELECTED]:
    RouteName.CLOUD_SECURITY_PERMISSIONS_LIST_TAB,
  [SecurityGapType.AGENT_REBOOT_REQUIRED]: RouteName.DEVICES,
  [SecurityGapType.AGENT_CONFLICT]: RouteName.DEVICES,
  [SecurityGapType.OUTDATED_AGENT]: RouteName.DEVICES,
  [SecurityGapType.INITIAL_USER_DATA_GOVERNANCE_CONFIGURATION_INCOMPLETE]:
    RouteName.USER_DATA_GOVERNANCE_MONITORING_TAB,
  [SecurityGapType.USER_DATA_GOVERNANCE_TYPES_NOT_CONFIGURED]:
    RouteName.USER_DATA_GOVERNANCE_MONITORING_TAB,
  [SecurityGapType.UNLICENSED_DEVICES]: RouteName.MSP_GLOBAL_WORKSPACE_MANAGEMENT_PAGE,
  [SecurityGapType.NO_MANAGED_SERVICE_CONTACTS_ADDED]: RouteName.SOC_CONTACTS_TAB,
};

const securityGapTypeQueryMap: Partial<Record<SecurityGapType, Record<string, string>>> = {
  [SecurityGapType.AGENT_REBOOT_REQUIRED]: {
    agentHealth: [AgentHealthIssue.AGENT_REQUIRES_RESTART].join(","),
  },
  [SecurityGapType.AGENT_CONFLICT]: {
    agentHealth: [AgentHealthIssue.AGENT_CONFLICT].join(","),
  },
  [SecurityGapType.OUTDATED_AGENT]: {
    agentHealth: [AgentHealthIssue.OUTDATED_AGENT].join(","),
  },
};

export default defineComponent({
  components: { TableWrapper },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<SecurityGapItem>>,
      required: true,
    },
  },
  emits: ["dismissed"],
  setup(props, { emit }) {
    const router = useRouter();
    const { t } = useI18n();
    const loading = ref(false);
    const { workspace } = storeToRefs(useWorkspaceStore());
    const securityGapsStore = useSecurityGapsStore();
    const { workspaces, totalWorkspaces } = storeToRefs(securityGapsStore);
    const { addVisitedWorkspaceId } = securityGapsStore;
    const localValue = ref({
      workspaces: [] as { name: string; workspaceId: string }[],
    });
    const workspaceSearch = ref("");
    const { setNavigationFromSecurityGaps } = useSecurityGapsNavigation();

    const isMspRoute = computed(() => isMspPortalRoute());

    const linkText = computed(() => {
      switch (props.config.item.securityGapType) {
        case SecurityGapType.AGENT_CONFLICT:
        case SecurityGapType.AGENT_REBOOT_REQUIRED:
        case SecurityGapType.OUTDATED_AGENT:
          return t("modals.manualFix.viewDevices");
        default:
          return t("modals.manualFix.goToSettings");
      }
    });

    const disclaimerText = computed(() => {
      switch (props.config.item.securityGapType) {
        case SecurityGapType.AGENT_CONFLICT:
        case SecurityGapType.AGENT_REBOOT_REQUIRED:
        case SecurityGapType.OUTDATED_AGENT:
          return "viewDevicesDisclaimer";
        default:
          return "goToSettingsDisclaimer";
      }
    });

    const goToSettings = async (workspaceId: string) => {
      const queryParams = securityGapTypeQueryMap[props.config.item.securityGapType] || {};

      if (
        isMspRoute.value &&
        props.config.item.securityGapType !== SecurityGapType.UNLICENSED_DEVICES
      ) {
        addVisitedWorkspaceId(workspaceId);
        setNavigationFromSecurityGaps(props.config.item, workspaceId);
        await navigateToRoute(
          workspaceId,
          securityGapTypeRouteMap[props.config.item.securityGapType] as string,
          queryParams
        );
        emit("dismissed");
      } else {
        const route = router.resolve({
          name: securityGapTypeRouteMap[props.config.item.securityGapType],
          query: { workspaceId, ...queryParams },
        });
        window.open(route.href, "_blank");
      }
    };

    onMounted(async () => {
      if (isMspRoute.value) {
        loading.value = true;
        await useSecurityGapsStore().getSecurityGapWorkspaces(props.config.item.securityGapType);
        localValue.value.workspaces = cloneDeep(workspaces.value);
        loading.value = false;
      }
    });

    watch(
      workspaceSearch,
      debounce((newVal) => {
        // TODO implement BE based search when BE will be ready
        loading.value = true;
        if (!newVal?.length) {
          localValue.value.workspaces = cloneDeep(workspaces.value);
        } else if (newVal.length > 2) {
          localValue.value.workspaces = workspaces.value.filter((v) =>
            v.name.toLowerCase().startsWith(newVal)
          );
        }
        loading.value = false;
      }, 500)
    );

    return {
      SecurityGapAction,
      workspaces,
      loading,
      totalWorkspaces,
      isMspRoute,
      linkText,
      disclaimerText,
      workspaceSearch,
      localValue,
      RouteName,
      workspace,
      goToSettings,
      SecurityGapType,
    };
  },
});
</script>

<style scoped lang="scss">
.v-table {
  border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
  box-shadow: none !important;
}

:deep(*) {
  .search-field {
    width: 280px;

    .v-input__control {
      width: 280px;
    }

    .v-field {
      background-color: rgb(var(--v-theme-indigo-faint)) !important;
    }

    .v-field__outline {
      display: none !important;
    }

    .icon-search-xs:before {
      color: rgb(var(--v-theme-indigo-medium));
    }
  }
}

.icon-success-xs:before {
  color: rgb(var(--v-theme-indigo-base)) !important;
}
</style>
